import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme, useMediaQuery } from '@mui/material';
import { businessData as businessDataAtom } from 'atoms';
import Blob from 'common/Blob';
import { logout } from 'components/auth/auth';
import routes from 'config/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { styled } from 'styled-components';

import React, { useState } from 'react';

const Div = styled.div`
    .MuiTypography-root {
        font-family: Poppins, serif !important;
        font-weight: 600 !important;
        font-size: 14px !important;
    }

    .heading {
        justify-content: center;
        text-align: center;
        font-family: 'Abril Fatface';
        font-size: 24px;
        font-style: normal;
        font-weight: 400;
        color: white;
        padding: 40px 75px;
    }

    .logout {
        position: absolute;
        bottom: 0px;
        left: 0px;
        right: 0px;
        width: 100%;
    }
    .copyright-text {
        font-size: 10px !important;
        font-weight: 500 !important;
        color: rgba(255, 255, 255, 0.30) !important;
    }
    .MuiListItem-gutters {
        padding: 6px 24px;
    }
    .menuItem {
        border: 1px solid #184353;
        border-radius: 16px;
        padding: 18px 18px;
        margin-bottom: 0;
        min-height: 60px;
        display: flex;
        align-items: center;
        transition: all 0.3s ease;
        width: 100% !important;
    }
    .menuItem:hover {
        background-color: rgba(0, 0, 0, 0.3);
    }
    .menuItem:hover .MuiListItemIcon-root svg path {
        fill: white !important;
    }
    .menuItem:hover .MuiTypography-root {
        color: white !important;
    }
    .menuItem.active {
        border: 1px solid #fff;
    }
`;

const drawerWidth = 280;

export default function SideNavBar({ isOpen = true, onClose }) {
    const location = useLocation();
    const pathName = location.pathname.split('/')[1];
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedIndex, setSelectedIndex] = useState(pathName);
    const navigate = useNavigate(); // Hook for navigation
    const [business, setBusiness] = useRecoilState(businessDataAtom);
    
    // Check if business exists - same logic as TabsStructure
    const isBusinessAdded = business?.email?.length > 0 && business?.id > 0;
    
    const handleClick = (key) => {
        // Redirect to business page if trying to access appointment/payment without business setup
        if ((key === 'appointment' || key === 'payment') && !isBusinessAdded) {
            setSelectedIndex('business');
            navigate('/business');
            // Close drawer on mobile/tablet after navigation
            if (isMobile && onClose) {
                onClose();
            }
            return;
        }
        
        setSelectedIndex(key);
        navigate(`/${key}`);
        // Hide HelpCrunch widget when switching tabs
        if (window.HelpCrunch) {
            window.HelpCrunch('hideChatWidget');
        }
        // Close drawer on mobile/tablet after navigation
        if (isMobile && onClose) {
            onClose();
        }
    };
    const handleLogout = async () => {
        const { success, error } = await logout();
        if (success) {
            localStorage.removeItem('business');
            localStorage.clear();
            // Redirect to the login page or refresh
            navigate(routes.auth.login);
            window.location.reload();
        } else {
            console.error(error.message);
        }
    };
    // Determine drawer variant based on screen size
    const drawerVariant = isMobile ? 'temporary' : 'persistent';
    
    // Drawer styling based on screen size
    const drawerSx = {
        width: isMobile ? drawerWidth : (isOpen ? drawerWidth : 0), // Reserve space only when open on desktop
        flexShrink: 0,
        transition: 'width 0.3s ease',
        overflow: 'hidden', // Hide drawer when width is 0
        '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#184353', // Ensure background color is preserved
            transition: 'transform 0.3s ease',
            // Apply all internal styles directly to ensure they work in mobile modal
            '& .heading': {
                justifyContent: 'center',
                textAlign: 'center',
                fontFamily: "'Abril Fatface', serif",
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 400,
                color: 'white',
                padding: '40px 75px',
            },
            '& .MuiTypography-root': {
                fontFamily: 'Poppins, serif !important',
                fontWeight: '600 !important',
                fontSize: '14px !important',
            },
            '& .MuiListItem-gutters': {
                padding: '6px 24px',
            },
            '& .menuItem': {
                border: '1px solid #184353',
                borderRadius: '16px',
                padding: '18px 18px',
                marginBottom: 0,
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                width: '100% !important',
            },
            '& .menuItem:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                '& .MuiListItemIcon-root svg path': {
                    fill: 'white !important',
                },
                '& .MuiTypography-root': {
                    color: 'white !important',
                },
            },
            '& .menuItem.active': {
                border: '1px solid #fff',
            },
            '& .logout': {
                position: 'absolute',
                bottom: '0px',
                left: '0px',
                right: '0px',
                width: '100%',
            },
            '& .copyright-text': {
                fontSize: '10px !important',
                fontWeight: '500 !important',
                color: 'rgba(255, 255, 255, 0.30) !important',
            },
            ...(isMobile ? {
                // Mobile/Tablet modal styling - only change outer positioning/margins/corners
                // Keep all internal content styles the same
                height: 'calc(100vh - 32px)',
                maxWidth: 'calc(100% - 32px)',
                margin: '16px',
                borderRadius: '16px',
            } : {
                // Desktop styling - fixed so it's docked and doesn't scroll
                position: 'fixed',
                height: '100vh',
                minHeight: '527px',
                top: 0,
                left: 0,
            }),
        },
    };

    return (
        <Div>
            <Drawer
                sx={drawerSx}
                variant={drawerVariant}
                anchor="left"
                open={isOpen}
                onClose={isMobile ? onClose : undefined}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile
                }}
            >
                <div className="heading cursor-pointer" onClick={() => navigate(routes.start)}>
                    Be:Seen
                </div>
                <List style={{ paddingBottom: '80px' }}>
                    <ListItem
                        key="business"
                        disablePadding
                        onClick={() => handleClick('business')}
                        style={{
                            color: selectedIndex === 'business' ? 'white' : '#789098',
                        }}
                    >
                        <ListItemButton className={`menuItem ${selectedIndex === 'business' ? 'active' : ''}`}>
                            <ListItemIcon>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g id="icon/city">
                                        <path
                                            id="Union"
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M9 1C7.89543 1 7 1.89543 7 3V12H5C3.89543 12 3 12.8954 3 14V20H2C1.44772 20 1 20.4477 1 21C1 21.5523 1.44772 22 2 22H5H7H9H19H22C22.5523 22 23 21.5523 23 21C23 20.4477 22.5523 20 22 20H21V3C21 1.89543 20.1046 1 19 1H9ZM19 20V3H9V14V20H19ZM7 14H5V17V20H7V17.8662C6.85291 17.9513 6.68214 18 6.5 18H6C5.44772 18 5 17.5523 5 17C5 16.4477 5.44772 16 6 16H6.5C6.68214 16 6.85291 16.0487 7 16.1338V14ZM13 5H11V7H13V5ZM15 5H17V7H15V5ZM13 8.5H11V10.5H13V8.5ZM15 8.5H17V10.5H15V8.5ZM17 12H15V14H17V12ZM15 15.5H17V17.5H15V15.5Z"
                                            fill={selectedIndex === 'business' ? 'white' : '#789098'}
                                        />
                                    </g>
                                </svg>
                            </ListItemIcon>
                            <ListItemText primary={business?.name || 'Your Business'} />
                        </ListItemButton>
                    </ListItem>

                    {isBusinessAdded && (
                        <ListItem
                            key="appointment"
                            disablePadding
                            onClick={() => handleClick('appointment')}
                            style={{
                                color: selectedIndex === 'appointment' ? 'white' : '#789098',
                            }}
                        >
                        <ListItemButton className={`menuItem ${selectedIndex === 'appointment' ? 'active' : ''}`}>
                            <ListItemIcon>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g id="icon/calendar-thirty-two">
                                        <path
                                            id="Vector (Stroke)"
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.5 1C9.05228 1 9.5 1.44772 9.5 2V3H14.5V2C14.5 1.44772 14.9477 1 15.5 1C16.0523 1 16.5 1.44772 16.5 2V3H22C22.5523 3 23 3.44772 23 4V22C23 22.5523 22.5523 23 22 23H2C1.44772 23 1 22.5523 1 22V4C1 3.44772 1.44772 3 2 3H7.5V2C7.5 1.44772 7.94772 1 8.5 1ZM7.5 5H3V21H21V5H16.5V6C16.5 6.55228 16.0523 7 15.5 7C14.9477 7 14.5 6.55228 14.5 6V5H9.5V6C9.5 6.55228 9.05228 7 8.5 7C7.94772 7 7.5 6.55228 7.5 6V5ZM5 10C5 9.44772 5.44772 9 6 9H10C10.5523 9 11 9.44772 11 10V17C11 17.5523 10.5523 18 10 18H6C5.44772 18 5 17.5523 5 17C5 16.4477 5.44772 16 6 16H9V14.5H7C6.44772 14.5 6 14.0523 6 13.5C6 12.9477 6.44772 12.5 7 12.5H9V11H6C5.44772 11 5 10.5523 5 10ZM13 10C13 9.44772 13.4477 9 14 9H18C18.5523 9 19 9.44772 19 10V17C19 17.5523 18.5523 18 18 18H14C13.4477 18 13 17.5523 13 17V10ZM15 11V16H17V11H15Z"
                                            fill={selectedIndex === 'appointment' ? 'white' : '#789098'}
                                        />
                                    </g>
                                </svg>
                            </ListItemIcon>
                            <ListItemText primary="Appointments" />
                        </ListItemButton>
                        </ListItem>
                    )}
                    {isBusinessAdded && (
                        <ListItem
                            key="payment"
                            disablePadding
                            onClick={() => handleClick('payment')}
                            style={{
                                color: selectedIndex === 'payment' ? 'white' : '#789098',
                            }}
                        >
                        <ListItemButton className={`menuItem ${selectedIndex === 'payment' ? 'active' : ''}`}>
                            <ListItemIcon>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g id="icon/paper-money-two">
                                        <path
                                            id="Vector (Stroke)"
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M1 6.5C1 5.94772 1.44772 5.5 2 5.5H22C22.5523 5.5 23 5.94772 23 6.5V18.5C23 19.0523 22.5523 19.5 22 19.5H2C1.44772 19.5 1 19.0523 1 18.5V6.5ZM3 11.4V13.6C4.95913 13.9977 6.5023 15.5409 6.89998 17.5H17.1C17.4977 15.5409 19.0409 13.9977 21 13.6V11.4C19.0409 11.0023 17.4977 9.45914 17.1 7.5H6.89998C6.5023 9.45914 4.95913 11.0023 3 11.4ZM4.8293 7.5H3V9.3293C3.85242 9.02801 4.52801 8.35242 4.8293 7.5ZM19.1707 7.5C19.472 8.35242 20.1476 9.02801 21 9.3293V7.5H19.1707ZM21 15.6707C20.1476 15.972 19.472 16.6476 19.1707 17.5H21V15.6707ZM4.8293 17.5C4.52801 16.6476 3.85242 15.972 3 15.6707V17.5H4.8293ZM8.5 12.5C8.5 10.4693 9.90405 8.5 12 8.5C14.096 8.5 15.5 10.4693 15.5 12.5C15.5 14.5307 14.096 16.5 12 16.5C9.90405 16.5 8.5 14.5307 8.5 12.5ZM12 10.5C11.3346 10.5 10.5 11.217 10.5 12.5C10.5 13.783 11.3346 14.5 12 14.5C12.6654 14.5 13.5 13.783 13.5 12.5C13.5 11.217 12.6654 10.5 12 10.5Z"
                                            fill={selectedIndex === 'payment' ? 'white' : '#789098'}
                                        />
                                    </g>
                                </svg>
                            </ListItemIcon>
                            <ListItemText primary="Payments" />
                        </ListItemButton>
                        </ListItem>
                    )}

                </List>
                <Blob></Blob>
                <List className="logout">
                    <ListItem disablePadding>
                        <ListItemText 
                            primary={`Copyright ${new Date().getFullYear()} Be Seen Health LLC. All rights reserved.`}
                            primaryTypographyProps={{
                                className: 'copyright-text',
                                style: {
                                    textAlign: 'center',
                                    padding: '16px 24px',
                                    fontFamily: 'Poppins, sans-serif'
                                }
                            }}
                        />
                    </ListItem>
                </List>
            </Drawer>
        </Div>
    );
}
